<script setup lang="ts">
import { ref, onMounted, onUnmounted, onBeforeMount, watch, computed } from 'vue';
import GeneralView from './GeneralView.vue';
import { useStyleStore } from "./store/styleStore";
import axiosInstance from './plugins/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useFavicon } from '@vueuse/core'
import { useRoute } from 'vue-router';
import { useHead } from '@unhead/vue'

const icon = useFavicon()
const route = useRoute();
const isPwaMode = ref(false);
const contentVisible = ref(false);
let socket: WebSocket | null = null;
const apiUrl = ref<string>(import.meta.env.VITE_CLIENT_API_BASE_URL as string)
const styleStore = useStyleStore();
const styles = ref(styleStore.getStyles());
const instanceIsActivated = ref(true);
const isPermittedUser = ref(false);
const backendToken = ref<string | null>(localStorage.getItem('token'));
const permittedUser = ref({})
const assetUrl = ref<string>(import.meta.env.VITE_CLIENT_ASSETS_URL as string)
const redirectPage = ref<string | null>(null);
const currentLang = ref(route.query.lang as string);

// Create reactive computed properties for meta data
const metaData = computed(() => {
  if (!styleStore.isStylesLoaded) {
    return {
      title: 'Loading...',
      description: '',
      keywords: '',
      author: '',
      themeColor: '',
      ogTitle: '',
      ogDescription: '',
      ogImage: '',
      favicon: ''
    }
  }

  return {
    title: styleStore.styles?.seoPageTitle?.value || 'AI Act Risk Navigator',
    description: styleStore.styles?.seoPageDescription?.value || '',
    keywords: styleStore.styles?.seoPageKeywords?.value || '',
    author: styleStore.styles?.seoPageAuthor?.value || '',
    themeColor: styleStore.styles?.seoThemeColor?.value || '',
    ogTitle: styleStore.styles?.ogTitle?.value || '',
    ogDescription: styleStore.styles?.ogDescription?.value || '',
    ogImage: assetUrl.value + (styleStore.styles?.ogImage?.value ?? ''),
    favicon: assetUrl.value + (styleStore.styles?.favicon?.value ?? '')
  }
})

// Call useHead once with reactive values
useHead({
  title: () => metaData.value.title,
  meta: () => [
    // Standard Meta Tags
    { name: 'robots', content: 'index, follow' },
    { name: 'description', content: metaData.value.description },
    { name: 'keywords', content: metaData.value.keywords },
    { name: 'author', content: metaData.value.author },
    { name: 'theme-color', content: metaData.value.themeColor },
    
    // Open Graph Meta Tags
    { name: 'og:type', content: 'website' },
    { property: 'og:site_name', content: metaData.value.ogTitle },
    { property: 'og:title', content: metaData.value.ogTitle },
    { property: 'og:description', content: metaData.value.ogDescription },
    { property: 'og:image', content: metaData.value.ogImage },
    { property: 'og:url', content: window.location.href },
    { property: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: metaData.value.ogTitle },
    { name: 'twitter:description', content: metaData.value.ogDescription },
    { name: 'twitter:image', content: metaData.value.ogImage },
    { name: 'twitter:url', content: window.location.href },
    
    // Language meta tag
    ...(currentLang.value ? [{ property: 'og:locale', content: currentLang.value + '_' + currentLang.value.toUpperCase() }] : [])
  ],
  link: () => [
    { rel: 'canonical', href: document.location.href },
    { rel: 'icon', href: metaData.value.favicon }
  ]
})

const checkAccess = async () => {
  icon.value = null;

  const instanceStatus = await checkInstanceActivation();
  instanceIsActivated.value = instanceStatus.active;
  redirectPage.value = instanceStatus.redirectPage;

  await styleStore.loadStyles();

  if (localStorage.getItem('token')) {
    isPermittedUser.value = await checkUserPermission();
  }

  if (!instanceIsActivated.value && !isPermittedUser.value) {
    console.log('Access denied. Redirecting to:', redirectPage.value);
    window.location.replace(redirectPage.value ?? '/');
  }
};

document.documentElement.lang = currentLang.value || 'en';

const allowedOrigins = [
  'http://localhost:5173',
  'https://staging.admin.tuev-risk-navigator.ai',
  'https://admin.tuev-risk-navigator.ai'
];

const handleTokenMessage = async (event: MessageEvent) => {
  if (!allowedOrigins.includes(event.origin)) {
    console.warn('Blocked message from untrusted origin:', event.origin);
    return;
  }

  if (event.data?.token) {
    localStorage.setItem('token', event.data.token);
    backendToken.value = event.data.token;

    await checkAccess();
    window.location.reload();
  }
};

const checkInstanceActivation = async () => {
  try {
    const res = await axiosInstance.get('/config/checkInstanceStatus');
    return res.data;
  } catch (error) {
    console.error(error);
    return false;
  }
}

const checkUserPermission = async () => {
  console.log('Checking user permission');
  try {
    const res = await axiosInstance.get('/config/checkUserPermission');
    permittedUser.value = res.data.user;
    return res.data.permitted;
  } catch (error) {
    console.error(error);
    return false;
  }
}

if (window.matchMedia('(display-mode: standalone)').matches) {
  isPwaMode.value = true;
}

onBeforeMount(() => {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    isPwaMode.value = true;
  }
});

onMounted(async () => {
  await checkAccess();

  window.addEventListener('message', handleTokenMessage);

  if (window.opener) {
    window.opener.postMessage('ready', '*');
  }

  window.addEventListener('storage', (event) => {
    if (event.key === 'token') {
      backendToken.value = event.newValue;
    }
  });

  if (isPwaMode.value) {
    contentVisible.value = false;
    setTimeout(() => {
      contentVisible.value = true;
    }, 3000);
  }

  axiosInstance.get('/visitors/countVisit');
});

// Update favicon when styles are loaded
watch(
  () => styleStore.isStylesLoaded,
  async (value) => {
    if (value) {
      await styleStore.loadStyles();
      const faviconPath = assetUrl.value + (styleStore.styles?.favicon?.value ?? '');

      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      link.href = faviconPath;

      document.head.appendChild(link);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  window.removeEventListener('message', handleTokenMessage);

  if (socket) {
    socket.close();
  }
});

/**
 * fix for weird primvue bug that adds padding because of the scrollbar on big screens
 * should be removed and handled properly (css) in the future
 */
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === "attributes" && mutation.attributeName === "style") {
      const body = document.body;
      const currentStyle = body.getAttribute("style");

      if (currentStyle?.includes("--p-scrollbar-width:")) {
        const newStyle = currentStyle.replace(/--p-scrollbar-width:\s*\d+px;?/g, "").trim();
        body.setAttribute("style", newStyle);
      }
    }
  });
});

observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });

window.addEventListener("load", () => {
  const body = document.body;
  const currentStyle = body.getAttribute("style");

  if (currentStyle?.includes("--p-scrollbar-width:")) {
    const newStyle = currentStyle.replace(/--p-scrollbar-width:\s*\d+px;?/g, "").trim();
    body.setAttribute("style", newStyle);
  }
});
/* end of fix */

// Update language meta tag when route changes
watch(
  () => route.query.lang,
  (newLang) => {
    currentLang.value = newLang as string;
    document.documentElement.lang = currentLang.value || 'en';
  },
  { immediate: true }
);

console.log('App mounted with styles:', styleStore.styles);
</script>

<template>
  <div>
    <div v-if="isPermittedUser && !instanceIsActivated">
      <Message closeable>
        <template #icon>
          <font-awesome-icon icon="info" />
        </template>
        <template #default>
          You are logged in as a backend user (<strong>{{ permittedUser?.email }}</strong>) and have access to this
          page.
          This page is not accessible to unauthorized users.
        </template>
      </Message>
    </div>

    <div v-if="instanceIsActivated || isPermittedUser">
      <div v-if="isPwaMode && !contentVisible" class="overlay">
        <div class="pwa-logo">
          <img :src="apiUrl + styles.frameworkLogo.value" alt="Logo">
        </div>
      </div>
      <div v-if="styleStore.isStylesLoaded">
        <main>
          <div class="backgroundCanvas">
            <div class="align-content-center bgImage justify-content-center">
              <img class="top-left-image" v-if="styleStore?.styles?.objectOneImage?.value"
                :src="assetUrl + styleStore?.styles?.objectOneImage?.value" loading="lazy" alt="image canvas left">
            </div>
            <div class="align-content-center bgImage justify-content-center">
              <img class="top-right-image" v-if="styleStore?.styles?.objectTwoImage?.value"
                :src="assetUrl + styleStore?.styles?.objectTwoImage?.value" loading="lazy" alt="image canvas right">
            </div>
            <div class="align-content-center bgImage justify-content-center">
              <img class="bottom-image" v-if="styleStore?.styles?.bottomImage?.value"
                :src="assetUrl + styleStore?.styles?.bottomImage?.value" loading="lazy" alt="stripes">
            </div>
          </div>
          <Suspense>
            <general-view></general-view>
          </Suspense>
          <Toast :autoZIndex="true" />
          <ConfirmPopup />
        </main>
      </div>
      <div v-else class="loading-screen">
        <div class="m-5 justify-content-center flex pt-5" style="flex-direction: column; align-items: center;">
          <div class="shapes flex"></div>
          <div class="flex mt-5 text-center text-white">
            The AI Act Risk Navigator is loading, please wait...
          </div>
        </div>
      </div>
    </div>
    <div v-if="!instanceIsActivated && !isPermittedUser" class="loading-screen">
      <div class="m-5 justify-content-center flex pt-5" style="flex-direction: column; align-items: center;">
        <div class="shapes flex"></div>
        <div class="flex mt-5 text-center text-white">
          The AI Act Risk Navigator is loading, please wait...
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Add your styles here */
</style>